import { useEffect, useMemo, useState } from "react";
import { TTemplate, TCropCoords } from "../../../../../../utils/types";
import { convertATemplateWithHeightToAnotherHeight } from "../../../../../../utils/functions";
import { cropImage } from "../../../../../../utils/image";
import { MAX_ZINDEX } from "../../../../../../utils/constant";
import {
  TPostSideBySideUnsortedBatchResponse,
  TSideBySideCensorship,
  TSideBySidePair,
  TSideBySideProcessedImage,
} from "../../../../../../services/api-clinic";
import {
  CENSORSHIP_STICKER_URL,
  getCensorshipStickerPlacements,
  getOuterContainerDimensions,
} from "../../template/censorshipStickers";

type Props = {
  templateEdit: TTemplate;
  beforeAfterResponse: TPostSideBySideUnsortedBatchResponse | null;
  width: number;
  initialTemplatesData?: TTemplateData[];
  initialSourceTemplateSignature?: string;
};

type TCensorshipSide = "before" | "after";

export type TTemplateData = {
  template: TTemplate;
  afterCroppedImage: string;
  beforeCroppedImage: string;
  afterOriginalImage: string;
  beforeOriginalImage: string;
  afterDimensions: { width: number; height: number };
  beforeDimensions: { width: number; height: number };
  afterCropCoords: TCropCoords;
  beforeCropCoords: TCropCoords;
  beforeImageId: number;
  afterImageId: number;
  beforeRotationDegrees: number;
  afterRotationDegrees: number;
  concat: {
    id_in_database: number;
  };
};

export function useTemplateData({
  templateEdit,
  beforeAfterResponse,
  width,
  initialTemplatesData,
  initialSourceTemplateSignature,
}: Props) {
  const currentTemplateSignature = JSON.stringify(templateEdit);
  const [templatesData, setTemplatesData] = useState<TTemplateData[]>(
    () => initialTemplatesData ?? [],
  );
  const [drawAllImages, setDrawAllImages] = useState(false);
  const [isRebasingTemplates, setIsRebasingTemplates] = useState(
    () =>
      initialTemplatesData !== undefined &&
      initialSourceTemplateSignature !== undefined &&
      initialSourceTemplateSignature !== currentTemplateSignature,
  );
  const beforeImageByUrl = useMemo(() => {
    if (!beforeAfterResponse)
      return new Map<string, TSideBySideProcessedImage>();
    const images = [
      ...beforeAfterResponse.allPairs.map((pair) => pair.before),
      ...beforeAfterResponse.unpairedBeforeImages,
    ];

    return new Map(images.map((image) => [image.processed_get_url, image]));
  }, [beforeAfterResponse]);
  const afterImageByUrl = useMemo(() => {
    if (!beforeAfterResponse)
      return new Map<string, TSideBySideProcessedImage>();
    const images = [
      ...beforeAfterResponse.allPairs.map((pair) => pair.after),
      ...beforeAfterResponse.unpairedAfterImages,
    ];

    return new Map(images.map((image) => [image.processed_get_url, image]));
  }, [beforeAfterResponse]);

  const createCensorshipImagesForSide = ({
    template,
    side,
    cropCoords,
    censorship,
    rotationDegrees,
  }: {
    template: TTemplate;
    side: TCensorshipSide;
    cropCoords?: TCropCoords;
    censorship?: TSideBySideCensorship | null;
    rotationDegrees?: number;
  }): TTemplate["images"] => {
    if (!cropCoords || !censorship) {
      return [];
    }

    const { width: outerWidth, height: outerHeight } =
      getOuterContainerDimensions(template.outerContainer);

    const placements = getCensorshipStickerPlacements({
      imageFrame:
        side === "before" ? template.beforeImage : template.afterImage,
      cropCoords,
      censorship,
      outerWidth,
      outerHeight,
      keyPrefix: side,
      rotationDegrees,
    });

    return placements.map((placement) => ({
      src: CENSORSHIP_STICKER_URL,
      kind: "censorship" as const,
      censorshipSide: side,
      regionKey: placement.regionKey,
      isUserAdjusted: false,
      position: { x: placement.left, y: placement.top },
      size: { width: placement.width, height: placement.height },
      containerStyle: {
        zIndex: MAX_ZINDEX - 2,
      },
      imageStyle: {
        width: "100%",
        height: "100%",
        objectFit: "fill",
      },
      preserveAspectRatio: false,
    }));
  };

  const mergeCensorshipImages = ({
    baseTemplate,
    previousTemplate,
    beforeCropCoords,
    afterCropCoords,
    beforeCensorship,
    afterCensorship,
    beforeRotationDegrees,
    afterRotationDegrees,
    recomputeSides,
  }: {
    baseTemplate: TTemplate;
    previousTemplate?: TTemplate;
    beforeCropCoords?: TCropCoords;
    afterCropCoords?: TCropCoords;
    beforeCensorship?: TSideBySideCensorship | null;
    afterCensorship?: TSideBySideCensorship | null;
    beforeRotationDegrees?: number;
    afterRotationDegrees?: number;
    recomputeSides: TCensorshipSide[];
  }): TTemplate => {
    const baseNonCensorshipImages = (baseTemplate.images || []).filter(
      (img) => img.kind !== "censorship",
    );
    const previousImages = previousTemplate?.images || [];

    const getMergedSideImages = (side: TCensorshipSide) => {
      const existingSideImages = previousImages.filter(
        (img) => img.kind === "censorship" && img.censorshipSide === side,
      );

      if (!recomputeSides.includes(side)) {
        return existingSideImages;
      }

      const userAdjustedImages = existingSideImages.filter(
        (img) => img.isUserAdjusted === true,
      );
      const regeneratedImages = createCensorshipImagesForSide({
        template: baseTemplate,
        side,
        cropCoords: side === "before" ? beforeCropCoords : afterCropCoords,
        censorship: side === "before" ? beforeCensorship : afterCensorship,
        rotationDegrees:
          side === "before" ? beforeRotationDegrees : afterRotationDegrees,
      });
      const adjustedRegionKeys = new Set(
        userAdjustedImages
          .map((image) => image.regionKey)
          .filter((regionKey): regionKey is string => Boolean(regionKey)),
      );

      return [
        ...regeneratedImages.filter(
          (image) =>
            !image.regionKey || !adjustedRegionKeys.has(image.regionKey),
        ),
        ...userAdjustedImages,
      ];
    };

    return {
      ...baseTemplate,
      images: [
        ...baseNonCensorshipImages,
        ...getMergedSideImages("before"),
        ...getMergedSideImages("after"),
      ],
    };
  };

  const stretchTemplate = async (
    afterImage: string,
    beforeImage: string,
    templateHeight: number,
  ) => {
    const afterImg = new Image();
    const beforeImg = new Image();

    const loadImage = (img: HTMLImageElement, url: string) =>
      new Promise<void>((res, rej) => {
        img.onload = () => res();
        img.onerror = rej;
        img.src = url;
      });

    await Promise.all([
      loadImage(afterImg, afterImage),
      loadImage(beforeImg, beforeImage),
    ]);

    const templateImageWidth = parseFloat(
      templateEdit.beforeImage.size.width.replace("%", ""),
    );
    const templateImageHeight = parseFloat(
      templateEdit.beforeImage.size.height.replace("%", ""),
    );
    const templateAspectRatio = templateImageWidth / templateImageHeight;

    const beforeAspectRatio = beforeImg.naturalWidth / beforeImg.naturalHeight;
    const afterAspectRatio = afterImg.naturalWidth / afterImg.naturalHeight;
    const pictureAspectRatio = (beforeAspectRatio + afterAspectRatio) / 2;

    const templateWidth = Math.floor(
      templateHeight * (pictureAspectRatio / templateAspectRatio),
    );
    const stretchXRatio = templateWidth / templateHeight;

    const stretched = JSON.parse(
      JSON.stringify(
        convertATemplateWithHeightToAnotherHeight(templateEdit, templateHeight),
      ),
    );

    stretched.outerContainer.width = templateWidth;
    stretched.afterImage.position.x *= stretchXRatio;
    stretched.beforeImage.position.x *= stretchXRatio;

    for (const img of stretched.images) {
      img.position.x *= stretchXRatio;
    }

    for (const text of stretched.texts) {
      text.position.x *= stretchXRatio;
      if (typeof text.textStyle.width === "number")
        text.textStyle.width *= stretchXRatio;
    }

    for (const div of stretched.divs) {
      if (typeof div.left === "number") div.left *= stretchXRatio;
    }

    return stretched;
  };

  const buildTemplateDataFromPair = async (pair: TSideBySidePair) => {
    const safeCropImage = async (
      url: string,
      cropCoords: TCropCoords,
      label: "before" | "after",
    ) => {
      try {
        return await cropImage(
          url,
          cropCoords.cropX,
          cropCoords.cropY,
          cropCoords.cropWidth,
          cropCoords.cropHeight,
        );
      } catch (error) {
        console.error(`[Step4][TemplateData] Failed to crop ${label} image`, {
          url,
          error,
        });
        // Do NOT fall back to the raw URL: handing it to html-to-image will
        // re-trigger the same CORS failure with a worse error message.
        throw error;
      }
    };

    const [afterCropped, beforeCropped] = await Promise.all([
      safeCropImage(
        pair.after.processed_get_url,
        pair.after.cropCoords,
        "after",
      ),
      safeCropImage(
        pair.before.processed_get_url,
        pair.before.cropCoords,
        "before",
      ),
    ]);

    const templateHeight = Math.min(500, width - 80) / 1.35;
    const template = await stretchTemplate(
      afterCropped,
      beforeCropped,
      templateHeight,
    );
    const templateWithCensorship = mergeCensorshipImages({
      baseTemplate: template,
      beforeCropCoords: pair.before.cropCoords,
      afterCropCoords: pair.after.cropCoords,
      beforeCensorship: pair.before.censorship || null,
      afterCensorship: pair.after.censorship || null,
      recomputeSides: ["before", "after"],
    });

    return {
      template: templateWithCensorship,
      afterCroppedImage: afterCropped,
      beforeCroppedImage: beforeCropped,
      afterOriginalImage: pair.after.processed_get_url,
      beforeOriginalImage: pair.before.processed_get_url,
      afterCropCoords: pair.after.cropCoords,
      beforeCropCoords: pair.before.cropCoords,
      afterDimensions: pair.after.imageDimensions,
      beforeDimensions: pair.before.imageDimensions,
      beforeImageId: pair.before.id_in_database,
      afterImageId: pair.after.id_in_database,
      beforeRotationDegrees: 0,
      afterRotationDegrees: 0,
      concat: pair.concat,
    };
  };

  useEffect(() => {
    if (!beforeAfterResponse) return;
    let cancelled = false;
    let drawTimeout: ReturnType<typeof setTimeout> | undefined;

    const finishRendering = (processed: TTemplateData[]) => {
      if (cancelled) return;
      setTemplatesData(processed);
      drawTimeout = setTimeout(() => {
        if (!cancelled) setDrawAllImages(true);
      }, 100);
    };

    if (initialTemplatesData !== undefined) {
      if (
        initialSourceTemplateSignature === undefined ||
        initialSourceTemplateSignature === currentTemplateSignature
      ) {
        setIsRebasingTemplates(false);
        return;
      }

      setIsRebasingTemplates(true);
      const rebasePreservedTemplates = async () => {
        const templateHeight = Math.min(500, width - 80) / 1.35;
        const rebasedResults = await Promise.allSettled(
          initialTemplatesData.map(async (item) => {
            const rebasedTemplate = await stretchTemplate(
              item.afterCroppedImage,
              item.beforeCroppedImage,
              templateHeight,
            );

            return {
              ...item,
              template: mergeCensorshipImages({
                baseTemplate: rebasedTemplate,
                previousTemplate: item.template,
                beforeCropCoords: item.beforeCropCoords,
                afterCropCoords: item.afterCropCoords,
                beforeCensorship:
                  beforeImageByUrl.get(item.beforeOriginalImage)?.censorship ||
                  null,
                afterCensorship:
                  afterImageByUrl.get(item.afterOriginalImage)?.censorship ||
                  null,
                beforeRotationDegrees: item.beforeRotationDegrees,
                afterRotationDegrees: item.afterRotationDegrees,
                recomputeSides: ["before", "after"],
              }),
            };
          }),
        );

        const rebased = rebasedResults.map((result, index) =>
          result.status === "fulfilled"
            ? result.value
            : initialTemplatesData[index],
        );
        finishRendering(rebased);
        if (!cancelled) setIsRebasingTemplates(false);
      };

      void rebasePreservedTemplates();
      return () => {
        cancelled = true;
        if (drawTimeout) clearTimeout(drawTimeout);
      };
    }

    const process = async () => {
      const processedResults = await Promise.allSettled(
        beforeAfterResponse.allPairs.map((pair: TSideBySidePair) =>
          buildTemplateDataFromPair(pair),
        ),
      );

      const processed = processedResults
        .filter(
          (
            result,
          ): result is PromiseFulfilledResult<
            Awaited<ReturnType<typeof buildTemplateDataFromPair>>
          > => result.status === "fulfilled",
        )
        .map((result) => result.value);

      if (processedResults.some((result) => result.status === "rejected")) {
        console.warn(
          "[Step4][TemplateData] Some mock pairs failed to render",
          processedResults,
        );
      }

      finishRendering(processed);
    };

    void process();
    return () => {
      cancelled = true;
      if (drawTimeout) clearTimeout(drawTimeout);
    };
  }, [
    beforeAfterResponse,
    currentTemplateSignature,
    initialSourceTemplateSignature,
    initialTemplatesData,
  ]);

  const appendTemplatePair = async (pair: TSideBySidePair) => {
    const newTemplateData = await buildTemplateDataFromPair(pair);
    setTemplatesData((prevData) => [...prevData, newTemplateData]);
  };

  const updateBeforeCroppedImage = async (
    image: string,
    index: number,
    cropCoords?: TCropCoords,
    selectedImage?: TSideBySideProcessedImage,
    rotationDegrees: number = 0,
  ) => {
    console.log("🟢 Updating Before Image", { index, cropCoords });

    // This adjusts the template height based on the width of the screen
    const current = templatesData[index];
    const templateHeight = Math.min(500, width - 80) / 1.35;

    const newTemplate = await stretchTemplate(
      current.afterCroppedImage,
      image,
      templateHeight,
    );

    const resolvedBeforeImage =
      selectedImage ||
      beforeImageByUrl.get(image) ||
      beforeImageByUrl.get(current.beforeOriginalImage);
    const nextBeforeOriginalImage = resolvedBeforeImage
      ? image
      : current.beforeOriginalImage;
    const resolvedCropCoords =
      cropCoords || resolvedBeforeImage?.cropCoords || current.beforeCropCoords;
    const templateWithCensorship = mergeCensorshipImages({
      baseTemplate: newTemplate,
      previousTemplate: current.template,
      beforeCropCoords: resolvedCropCoords ?? current.beforeCropCoords,
      afterCropCoords: current.afterCropCoords,
      beforeCensorship: resolvedBeforeImage?.censorship || null,
      afterCensorship:
        afterImageByUrl.get(current.afterOriginalImage)?.censorship || null,
      beforeRotationDegrees: rotationDegrees,
      recomputeSides: ["before"],
    });
    const newTemplatesData = templatesData.map((item, i) =>
      i === index
        ? {
            ...item,
            beforeCroppedImage: image,
            beforeCropCoords: resolvedCropCoords ?? item.beforeCropCoords,
            beforeDimensions:
              resolvedBeforeImage?.imageDimensions ?? item.beforeDimensions,
            beforeOriginalImage: nextBeforeOriginalImage,
            beforeRotationDegrees: rotationDegrees,
            template: templateWithCensorship,
          }
        : item,
    );

    // console.log("🟢 New Templates Data in updateBeforeCroppedImage:", newTemplatesData);
    // console.log("Updated index: ", index, "beforeOriginalImage: ", image);
    setTemplatesData(newTemplatesData);
  };

  const updateAfterCroppedImage = async (
    image: string,
    index: number,
    cropCoords?: TCropCoords,
    selectedImage?: TSideBySideProcessedImage,
    rotationDegrees: number = 0,
  ) => {
    console.log("🟢 Updating After Image", { index, cropCoords });

    const current = templatesData[index];
    const templateHeight = Math.min(500, width - 80) / 1.35;

    const newTemplate = await stretchTemplate(
      image,
      current.beforeCroppedImage,
      templateHeight,
    );

    const resolvedAfterImage =
      selectedImage ||
      afterImageByUrl.get(image) ||
      afterImageByUrl.get(current.afterOriginalImage);
    const nextAfterOriginalImage = resolvedAfterImage
      ? image
      : current.afterOriginalImage;
    const resolvedCropCoords =
      cropCoords || resolvedAfterImage?.cropCoords || current.afterCropCoords;
    const templateWithCensorship = mergeCensorshipImages({
      baseTemplate: newTemplate,
      previousTemplate: current.template,
      beforeCropCoords: current.beforeCropCoords,
      afterCropCoords: resolvedCropCoords ?? current.afterCropCoords,
      beforeCensorship:
        beforeImageByUrl.get(current.beforeOriginalImage)?.censorship || null,
      afterCensorship: resolvedAfterImage?.censorship || null,
      afterRotationDegrees: rotationDegrees,
      recomputeSides: ["after"],
    });
    const newTemplatesData = templatesData.map((item, i) =>
      i === index
        ? {
            ...item,
            afterCroppedImage: image,
            afterCropCoords: resolvedCropCoords ?? item.afterCropCoords,
            afterDimensions:
              resolvedAfterImage?.imageDimensions ?? item.afterDimensions,
            afterOriginalImage: nextAfterOriginalImage,
            afterRotationDegrees: rotationDegrees,
            template: templateWithCensorship,
          }
        : item,
    );
    // console.log("🟢 New Templates Data in updateAfterCroppedImage:", newTemplatesData);
    // console.log("Updated index: ", index, "afterOriginalImage: ", image);

    setTemplatesData(newTemplatesData);
  };

  const updateBothCroppedImages = async (
    beforeImage: string,
    afterImage: string,
    index: number,
    beforeCropCoords?: TCropCoords,
    afterCropCoords?: TCropCoords,
    beforeRotationDegrees: number = 0,
    afterRotationDegrees: number = 0,
  ) => {
    console.log("🟢 Updating Both Images", {
      index,
      beforeCropCoords,
      afterCropCoords,
    });

    // This adjusts the template height based on the width of the screen
    const current = templatesData[index];
    const templateHeight = Math.min(500, width - 80) / 1.35;

    // Stretch template with both new images at once
    const newTemplate = await stretchTemplate(
      afterImage,
      beforeImage,
      templateHeight,
    );
    const nextBeforeCropCoords = beforeCropCoords ?? current.beforeCropCoords;
    const nextAfterCropCoords = afterCropCoords ?? current.afterCropCoords;
    const templateWithCensorship = mergeCensorshipImages({
      baseTemplate: newTemplate,
      previousTemplate: current.template,
      beforeCropCoords: nextBeforeCropCoords,
      afterCropCoords: nextAfterCropCoords,
      beforeCensorship:
        beforeImageByUrl.get(current.beforeOriginalImage)?.censorship || null,
      afterCensorship:
        afterImageByUrl.get(current.afterOriginalImage)?.censorship || null,
      beforeRotationDegrees,
      afterRotationDegrees,
      recomputeSides: ["before", "after"],
    });

    const newTemplatesData = templatesData.map((item, i) =>
      i === index
        ? {
            ...item,
            beforeCroppedImage: beforeImage,
            afterCroppedImage: afterImage,
            beforeCropCoords: nextBeforeCropCoords,
            afterCropCoords: nextAfterCropCoords,
            beforeRotationDegrees,
            afterRotationDegrees,
            template: templateWithCensorship,
          }
        : item,
    );

    setTemplatesData(newTemplatesData);
  };

  return {
    templatesData,
    setTemplatesData,
    drawAllImages,
    setDrawAllImages,
    updateBeforeCroppedImage,
    updateAfterCroppedImage,
    updateBothCroppedImages,
    appendTemplatePair,
    isRebasingTemplates,
  };
}
